import {
  createRoute,
  Link,
  Outlet,
  redirect,
  useRouterState,
} from "@tanstack/react-router";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Skeleton } from "@/components/ui/skeleton";
import { CaseLevelTabs } from "@/components/case/case-level-tabs";
import { CaseWorkspaceHeader } from "@/components/case/case-workspace-header";
import { useCaseDetail } from "@/hooks/use-case-detail";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { rootRoute } from "../root-route";

export const caseLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/cases/$caseId",
  beforeLoad: async ({ params, location }) => {
    try {
      await api.me();
    } catch {
      throw redirect({
        to: "/login",
        search: { returnTo: location.pathname, error: undefined },
      });
    }
    // Bare /cases/:id → versions table
    const base = `/cases/${params.caseId}`;
    if (location.pathname === base || location.pathname === `${base}/`) {
      throw redirect({
        to: "/cases/$caseId/versions",
        params: { caseId: params.caseId },
      });
    }
  },
  component: CaseLayoutPage,
});

function CaseLayoutPage() {
  const auth = useAuth();
  const { caseId } = caseLayoutRoute.useParams();
  const detailQuery = useCaseDetail(caseId);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  // Version detail paths: /cases/:id/versions/:versionId/...
  const onVersionDetail = new RegExp(
    `^/cases/${caseId.replace(/[.*+?^${}()|[\\]\\\\]/g, "\\$&")}/versions/[^/]+`,
  ).test(pathname);

  if (auth.status === "loading" || detailQuery.isLoading) {
    return (
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-5">
          <div className="flex flex-col">
            <Skeleton className="h-3.5 w-40" />
            <div className="mt-4 flex items-center justify-between gap-4">
              <Skeleton className="h-7 w-56" />
              <Skeleton className="h-7 w-28" />
            </div>
            <Skeleton className="mt-5 h-9 w-52" />
          </div>
          <Skeleton className="h-8 w-full max-w-lg" />
        </div>
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (auth.status === "anon") return null;

  if (detailQuery.isError || !detailQuery.data) {
    return (
      <div className="flex flex-col gap-4">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink render={<Link to="/" />}>案件</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>未找到</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <Alert variant="destructive">
          <AlertTitle>案件未找到</AlertTitle>
          <AlertDescription>
            案件未找到或无法加载。
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // Version layout renders its own header + tabs.
  if (onVersionDetail) {
    return <Outlet />;
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-5">
        <CaseWorkspaceHeader caseId={caseId} detail={detailQuery.data} />
        <CaseLevelTabs caseId={caseId} />
      </div>
      <Outlet />
    </div>
  );
}
